/**
 * music-sequencer/bar-phase.js — на какой из найденных долей песни стоит
 * «раз» такта («Бит по песне», /music-sequencer).
 *
 * ЗАЧЕМ. BeatGrid находит доли, но не такт: какая из четырёх долей первая, он
 * не знает. До 26.09.2026 шаг 0 бита вставал на первую ЗВУЧАЩУЮ долю песни, и
 * если песня начиналась не с «раз» (затакт, вступление с середины такта,
 * тихая первая доля), бочка бита всю песню стучала на «два» или «три»: каждая
 * доля на месте, а бит «не в такт».
 *
 * ЗАМЕР (26.09.2026). Эталон — Beat This! (CPJKU, ISMIR 2024) на 113 песнях:
 * 51 наш пример стилей (Suno) и 62 песни JamendoLyrics. Доли — наш BeatGrid, как
 * в инструменте; уровень — тот, что человек ставит кнопками ½ / ×2. Оценены 85
 * песен в 4/4. Кандидаты фазы — ровно те, что принимает followMap (шаг
 * min(1, 1/ratio) доли). «Верно» — выбран тот же кандидат, на котором ложится
 * больше всего «раз» эталона.
 *                                              верно     «раз» эталона под «раз» бита
 *   первая звучащая доля (было до 26.09) ...  44/85         44 %
 *   этот модуль, 44.1 кГц ..................  69/85 (81 %)  73 %   (потолок сетки 78 %)
 *   этот модуль, 48 кГц ....................  68/85
 *   Веса подобраны на одном корпусе и проверены на другом: Suno → Jamendo
 *   43/55, Jamendo → Suno 24/30 (у первой звучащей доли там 34/55 и 10/30).
 *   Отвергнуто замером: «малый барабан на 2 и 4» (полоса 1–4 кГц ловит всё
 *   подряд: одна угадывает 25 %, в сумме только мешает), громкость на доле
 *   (18 %), смена гармонии через ДОЛЮ, а не через такт (58 %).
 *   Отрыв лучшего кандидата (margin) — честная уверенность: в нижней четверти
 *   песен по отрыву верно 14 из 21, в верхней 21 из 22.
 *
 * КАК. Кандидат фазы → сетка четвертей от него → на каждой четверти три
 * признака: насколько хрома (80 Гц – 2 кГц) такта ПОСЛЕ отличается от такта ДО,
 * то же для баса (40–250 Гц) и всплеск низа 30–120 Гц (бочка). Признаки
 * приводятся к z внутри песни; очки кандидата = z(гармония) + z(бас) на его
 * «раз» + 0.5·(z(бочка) на «раз» и на «три»).
 * Спектр — окно 93 мс, как в исследовании, но сигнал сначала прорежен до
 * ~5.5 кГц (всё нужное лежит ниже 2 кГц: БПФ длиной 512, а не 2048), шаг 23 мс
 * вместо 11.6 и фильтр 40 дБ вместо 50: на всех 85 песнях выбор тот же, а
 * работы втрое меньше — песня в 3.3 минуты стоит ~0.3 с в воркере (настольный
 * процессор).
 *
 * Зависимости: DspFftMixed (dsp-fft-mixed.js) и MSTimeline.beatTime — та же
 * интерполяция долей, по которой бит играет (сетка признаков обязана совпадать
 * с сеткой звучания). В воркере и в Node их можно передать в opts.
 *
 * Сторожа: tests/js/music-sequencer-bar-phase.test.mjs (песня с известным
 * «раз» на всех уровнях ½ / ×2, обе частоты; проверен возвратом дефекта) и
 * проверка E в tests/playwright/music-sequencer-song-plays-once.mjs (воркер на
 * настоящей странице). Корпусный замер — разовый: эталону нужен CUDA.
 */
(function (root, factory) {
    var api = factory(root);
    if (typeof module === 'object' && module.exports) module.exports = api;
    else if (root) root.MSBarPhase = api;
})(typeof self !== 'undefined' ? self : this, function (root) {
    'use strict';

    /* Окно исследования — 2048 при 22 050 Гц; шаг вдвое крупнее (см. шапку).
       Масштаб амплитуды приводится к окну исследования: log1p(10·|X|) нелинеен,
       и без этого всплеск бочки зависел бы от частоты дискретизации. */
    var WIN_S = 2048 / 22050, HOP_S = 512 / 22050, REF_WIN_SUM = (2048 - 1) / 2;
    var DECIM_RATE = 5512.5, MIN_RATE = 4400, TOP_HZ = 2000;
    var KICK = [30, 120], HARM = [80, 2000], BASS = [40, 250];
    var ONSET_WIN_S = 0.04;
    var W_HARM = 1, W_BASS = 1, W_KICK13 = 0.5;
    /* Четверти по краям сетки: такту «до» и «после» там не хватает места. */
    var EDGE = 4;
    /* Уровни, до которых доходят ½ / ×2 (nudgeFollow держит 0.25…4). */
    var RATIOS = [0.25, 0.5, 1, 2, 4];
    /* Шаг 0 бита — первый «раз», не раньше чем за четверть до первой звучащей
       доли: затакт в одну четверть звучит до бита, а «раз», у которого тихая
       атака, не пропускается. «Звучит» — от −26 дБ к типичной доле песни.
       Замер (фаза заведомо верная, «раз» эталона = первый такт песни, 85 песен):
         прежнее правило, порог 0.35 без допуска ......... 40 — на месте
         0.35 + четверть .................................. 50
         0.05 + четверть .................................. 78 (на такт позже 6, раньше 1)
       Позже — хуже, чем раньше: сдвиг «бит с такта N» двигает начало только
       вперёд, опоздание человек не исправит. */
    var START_TOL_Q = 1, SOUNDING_K = 0.05;

    function now() { return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); }

    function besselI0(x) {
        var s = 1, t = 1, y = x * x / 4;
        for (var k = 1; k < 64; k++) { t *= y / (k * k); s += t; if (t < s * 1e-12) break; }
        return s;
    }

    /** ФНЧ перед прореживанием: окно Кайзера, 40 дБ там, откуда отражение
     *  легло бы ниже TOP_HZ (выше rate − TOP_HZ). */
    function decimFilter(sr, rate) {
        var fpass = TOP_HZ, fstop = rate - TOP_HZ;
        var dw = 2 * Math.PI * (fstop - fpass) / sr, A = 40, beta = 0.1102 * (A - 8.7);
        var m = Math.ceil((A - 8) / (2.285 * dw)) | 1;
        var fc = (fpass + fstop) / 2 / sr, half = (m - 1) / 2, i0b = besselI0(beta);
        var h = new Float64Array(m), sum = 0, i;
        for (i = 0; i < m; i++) {
            var x = i - half, r = x / half;
            var sinc = x === 0 ? 2 * fc : Math.sin(2 * Math.PI * fc * x) / (Math.PI * x);
            h[i] = sinc * besselI0(beta * Math.sqrt(Math.max(0, 1 - r * r))) / i0b;
            sum += h[i];
        }
        for (i = 0; i < m; i++) h[i] /= sum;
        return h;
    }

    function decimFactor(sr) {
        var d = Math.max(1, Math.round(sr / DECIM_RATE));
        while (d > 1 && sr / d < MIN_RATE) d--;
        return d;
    }

    /** Прореживание в d раз с фазово-линейным ФНЧ: отсчёт j стоит на времени
     *  j·d / sr, без задержки. */
    function decimate(pcm, sr, d) {
        if (d <= 1) return pcm;
        var h = decimFilter(sr, sr / d), m = h.length, half = (m - 1) >> 1, len = pcm.length;
        var n = Math.floor((len - 1) / d) + 1, out = new Float32Array(n);
        for (var j = 0; j < n; j++) {
            var k0 = j * d + half, a0 = 0, a1 = 0, a2 = 0, a3 = 0;
            var kmin = k0 - (len - 1) > 0 ? k0 - (len - 1) : 0, kmax = k0 < m - 1 ? k0 : m - 1, k = kmin;
            /* Четыре независимые суммы вместо одной цепочки сложений
               (замер: прореживание 3.3-минутной песни 195 → 150 мс). */
            for (; k + 3 <= kmax; k += 4) {
                var p = k0 - k;
                a0 += h[k] * pcm[p]; a1 += h[k + 1] * pcm[p - 1]; a2 += h[k + 2] * pcm[p - 2]; a3 += h[k + 3] * pcm[p - 3];
            }
            for (; k <= kmax; k++) a0 += h[k] * pcm[k0 - k];
            out[j] = (a0 + a1) + (a2 + a3);
        }
        return out;
    }

    function pitchClass(f) {
        var r = Math.round(12 * Math.log(f / 440) / Math.LN2) % 12;
        return r < 0 ? r + 12 : r;
    }

    /**
     * Признаки кадров песни.
     * @param {Float32Array} pcm моно
     * @param {number} sr
     * @param {{fft?:object, decimate?:boolean, frame?:number, hop?:number}} [opts]
     * @returns {{rate:number, frame:number, hop:number, n:number, win:number,
     *   kick:Float32Array, cC:Float64Array, cB:Float64Array}} cC/cB — суммы
     *   хромы с начала песни, (n+1)×12: среднее за любой отрезок — две строки.
     */
    function features(pcm, sr, opts) {
        opts = opts || {};
        var F = opts.fft || (root && root.DspFftMixed);
        if (!F || typeof F.plan !== 'function') throw new Error('bar-phase: DspFftMixed missing');
        var d = opts.decimate === false ? 1 : decimFactor(sr);
        var x = decimate(pcm, sr, d), rate = sr / d;
        var N = opts.frame || Math.max(256, Math.min(4096, Math.pow(2, Math.round(Math.log(WIN_S * rate) / Math.LN2))));
        var hop = opts.hop || Math.max(1, Math.round(HOP_S * rate));
        var n = x.length >= N ? 1 + Math.floor((x.length - N) / hop) : 0;
        var binHz = rate / N, kTop = Math.min(N >> 1, Math.ceil(TOP_HZ / binHz) + 1);
        var w = new Float64Array(N), wsum = 0, i, k;
        for (i = 0; i < N; i++) { w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (N - 1)); wsum += w[i]; }
        var scale = REF_WIN_SUM / wsum;
        var isKick = new Uint8Array(kTop), pcH = new Int8Array(kTop), pcB = new Int8Array(kTop);
        for (k = 0; k < kTop; k++) {
            var f = k * binHz;
            isKick[k] = f >= KICK[0] && f < KICK[1] ? 1 : 0;
            pcH[k] = f > 0 && f >= HARM[0] && f < HARM[1] ? pitchClass(f) : -1;
            pcB[k] = f > 0 && f >= BASS[0] && f < BASS[1] ? pitchClass(f) : -1;
        }
        var plan = F.plan(N);
        var re = new Float64Array(N), im = new Float64Array(N);
        var ar = new Float64Array(kTop), ai = new Float64Array(kTop), br = new Float64Array(kTop), bi = new Float64Array(kTop);
        var prevL = new Float64Array(kTop);
        var kick = new Float32Array(n), cC = new Float64Array((n + 1) * 12), cB = new Float64Array((n + 1) * 12);
        var ch = new Float64Array(12), chB = new Float64Array(12);

        function frame(idx, sr_, si_) {
            var flux = 0, c;
            for (c = 0; c < 12; c++) { ch[c] = 0; chB[c] = 0; }
            for (var q = 1; q < kTop; q++) {
                var mag = Math.sqrt(sr_[q] * sr_[q] + si_[q] * si_[q]) * scale;
                if (isKick[q]) {
                    var L = Math.log(1 + 10 * mag);
                    if (idx > 0 && L > prevL[q]) flux += L - prevL[q];
                    prevL[q] = L;
                }
                if (pcH[q] >= 0) ch[pcH[q]] += 10 * mag;
                if (pcB[q] >= 0) chB[pcB[q]] += 10 * mag;
            }
            kick[idx] = flux;
            var o = idx * 12, o2 = o + 12;
            for (c = 0; c < 12; c++) { cC[o2 + c] = cC[o + c] + ch[c]; cB[o2 + c] = cB[o + c] + chB[c]; }
        }

        for (var fr = 0; fr < n; fr += 2) {
            var a0 = fr * hop, b0 = a0 + hop, two = fr + 1 < n;
            for (i = 0; i < N; i++) { re[i] = x[a0 + i] * w[i]; im[i] = two ? x[b0 + i] * w[i] : 0; }
            F.fft(plan, re, im);
            F.untangleTwoReal(re, im, N, kTop, ar, ai, br, bi);
            frame(fr, ar, ai);
            if (two) frame(fr + 1, br, bi);
        }
        return { rate: rate, frame: N, hop: hop, n: n, win: Math.floor(ONSET_WIN_S * rate / hop) + 1,
            kick: kick, cC: cC, cB: cB };
    }

    /* Номер первого кадра с центром не раньше t (searchsorted исследования). */
    function frameAt(feat, t) {
        var i = Math.ceil((t * feat.rate - feat.frame / 2) / feat.hop);
        return i < 0 ? 0 : i > feat.n - 1 ? feat.n - 1 : i;
    }
    function onset(feat, t) {
        var i = frameAt(feat, t), a = Math.max(0, i - feat.win), b = Math.min(feat.n - 1, i + feat.win), m = 0;
        for (var j = a; j <= b; j++) if (feat.kick[j] > m) m = feat.kick[j];
        return m;
    }
    function segInto(cs, feat, a, b, out) {
        var ia = frameAt(feat, a), ib = frameAt(feat, b), nrm = 0, c;
        if (ib <= ia) ib = ia + 1;
        for (c = 0; c < 12; c++) { out[c] = (cs[ib * 12 + c] - cs[ia * 12 + c]) / (ib - ia); nrm += out[c] * out[c]; }
        nrm = Math.sqrt(nrm);
        if (nrm > 0) for (c = 0; c < 12; c++) out[c] /= nrm;
    }
    var _u = new Float64Array(12), _v = new Float64Array(12);
    function change(cs, feat, a, t, b) {
        segInto(cs, feat, a, t, _u); segInto(cs, feat, t, b, _v);
        var dot = 0;
        for (var c = 0; c < 12; c++) dot += _u[c] * _v[c];
        return 1 - dot;
    }

    function zNorm(v, a, b) {
        var n = b - a, s = 0, s2 = 0, i;
        if (n <= 0) return;
        for (i = a; i < b; i++) s += v[i];
        var mean = s / n;
        for (i = a; i < b; i++) s2 += (v[i] - mean) * (v[i] - mean);
        var sd = Math.sqrt(s2 / n);
        for (i = 0; i < v.length; i++) v[i] = sd > 0 ? (v[i] - mean) / sd : 0;
    }
    function meanAt(v, pos, a, b) {
        var s = 0, c = 0;
        for (var i = a; i < b; i++) if ((i & 3) === pos) { s += v[i]; c++; }
        return c ? s / c : 0;
    }

    function quarterGrid(beats, ratio, j0, tEnd, beatTime) {
        var out = [];
        for (var k = 0; k <= 20000; k++) {
            var t = beatTime(beats, j0 + k / ratio);
            if (t > tEnd) break;
            if (t >= 0) out.push(t);
        }
        return out;
    }

    /** Очки одного кандидата (null — сетка короче двух тактов на краях). */
    function scoreCandidate(feat, beats, ratio, j0, tEnd, beatTime) {
        var Q = quarterGrid(beats, ratio, j0, tEnd, beatTime), nq = Q.length;
        if (nq < 2 * EDGE + 4) return null;
        var kick = new Float64Array(nq), harm = new Float64Array(nq), bass = new Float64Array(nq);
        for (var i = EDGE; i < nq - EDGE; i++) {
            kick[i] = onset(feat, Q[i]);
            harm[i] = change(feat.cC, feat, Q[i - 4], Q[i], Q[i + 4]);
            bass[i] = change(feat.cB, feat, Q[i - 4], Q[i], Q[i + 4]);
        }
        var a = EDGE, b = nq - EDGE;
        zNorm(kick, a, b); zNorm(harm, a, b); zNorm(bass, a, b);
        return W_HARM * meanAt(harm, 0, a, b) + W_BASS * meanAt(bass, 0, a, b) +
            W_KICK13 * (meanAt(kick, 0, a, b) + meanAt(kick, 2, a, b));
    }

    function beatTimeOf(opts) {
        var bt = (opts && opts.beatTime) || (root && root.MSTimeline && root.MSTimeline.beatTime);
        if (typeof bt !== 'function') throw new Error('bar-phase: MSTimeline.beatTime missing');
        return bt;
    }

    /**
     * Фаза «раз» для уровня ratio.
     * @returns {{phase:number, margin:number, bar:number, step:number, scores:Array<number|null>}|null}
     *   phase — номер доли песни (дробный при ratio > 1) в [0, bar);
     *   margin — отрыв лучшего кандидата от второго (в z; 0 — выбора не было).
     */
    function pickPhase(beats, ratio, feat, tEnd, opts) {
        var bt = beatTimeOf(opts);
        var step = Math.min(1, 1 / ratio), nc = Math.round((4 / ratio) / step);
        var best = -Infinity, second = -Infinity, phase = 0, scores = [];
        for (var c = 0; c < nc; c++) {
            var s = scoreCandidate(feat, beats, ratio, c * step, tEnd, bt);
            scores.push(s);
            if (s === null) continue;
            if (s > best) { second = best; best = s; phase = c * step; } else if (s > second) second = s;
        }
        if (best === -Infinity) return null;
        return { phase: phase, margin: second === -Infinity ? 0 : best - second, bar: 4 / ratio, step: step, scores: scores };
    }

    /** Первая доля, где музыка действительно играет (трекер ставит доли и в
     *  тишине перед песней): RMS ±50 мс вокруг доли не ниже SOUNDING_K медианы. */
    function firstSounding(pcm, sr, beats) {
        var half = Math.round(0.05 * sr), rms = [], i;
        for (i = 0; i < beats.length; i++) {
            var c = Math.round(beats[i] * sr), a = Math.max(0, c - half), b = Math.min(pcm.length, c + half), e = 0;
            for (var k = a; k < b; k += 2) e += pcm[k] * pcm[k];
            rms.push(Math.sqrt(e / Math.max(1, (b - a) / 2)));
        }
        var sorted = rms.slice().sort(function (x, y) { return x - y; }), med = sorted[sorted.length >> 1] || 0;
        for (i = 0; i < rms.length; i++) if (rms[i] >= med * SOUNDING_K && rms[i] > 0) return i;
        return 0;
    }

    /** Шаг 0 бита: первый «раз» фазы phase не раньше fs − tolQ четвертей. */
    function startIndex(phase, ratio, fs, tolQ) {
        var bar = 4 / ratio, tol = (tolQ == null ? START_TOL_Q : tolQ) / ratio;
        return phase + bar * Math.ceil((fs - tol - phase) / bar - 1e-9);
    }

    /**
     * Всё сразу — то, что воркер отдаёт вместе с сеткой долей.
     * @returns {{v:number, fs:number, byRatio:Object<string,{phase:number,j0:number,margin:number}>, ms:number}}
     */
    function analyze(pcm, sr, beats, opts) {
        var t0 = now(), feat = features(pcm, sr, opts), tEnd = pcm.length / sr;
        var fs = firstSounding(pcm, sr, beats), out = { v: 1, fs: fs, byRatio: {} };
        for (var i = 0; i < RATIOS.length; i++) {
            var r = RATIOS[i], p = pickPhase(beats, r, feat, tEnd, opts);
            if (p) out.byRatio[String(r)] = { phase: p.phase, j0: startIndex(p.phase, r, fs), margin: Math.round(p.margin * 1000) / 1000 };
        }
        out.ms = Math.round(now() - t0);
        return out;
    }

    return {
        RATIOS: RATIOS,
        START_TOL_Q: START_TOL_Q,
        features: features,
        pickPhase: pickPhase,
        firstSounding: firstSounding,
        startIndex: startIndex,
        analyze: analyze,
        _decimFilter: decimFilter,
        _decimate: decimate
    };
});
